import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { 
  UserPlus, 
  Loader2, 
  CheckCircle, 
  Copy,
  ArrowLeft,
  Mail,
  Phone,
  User
} from 'lucide-react';
import { useReseller } from '@/hooks/useReseller';
import { toast } from 'sonner';
import { cn } from '@/utils/functions/cn';

const createClientSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome muito longo'),
  email: z.string().email('Email inválido').min(1, 'Email é obrigatório'),
  phone: z.string().optional(),
  planId: z.string().min(1, 'Plano é obrigatório'),
  autoGenerate: z.boolean().default(true),
  customCode: z.string().optional()
}).refine((data) => {
  if (!data.autoGenerate && !data.customCode) {
    return false;
  }
  return true;
}, {
  message: "Código personalizado é obrigatório quando auto-geração está desabilitada",
  path: ["customCode"],
});

type CreateClientFormData = z.infer<typeof createClientSchema>;

interface CreateClientFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CreateClientForm = ({ onSuccess, onCancel }: CreateClientFormProps) => {
  const { dashboard, createClient, isCreatingClient } = useReseller();
  const [createdClient, setCreatedClient] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    reset
  } = useForm<CreateClientFormData>({
    resolver: zodResolver(createClientSchema),
    defaultValues: {
      autoGenerate: true
    }
  });

  const autoGenerate = watch('autoGenerate');

  const onSubmit = async (data: CreateClientFormData) => {
    try {
      const result = await new Promise<any>((resolve, reject) => {
        createClient(data, {
          onSuccess: resolve,
          onError: reject
        });
      });
      
      setCreatedClient(result);
    } catch (error) {
      console.error('Error creating client:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  const handleCreateAnother = () => {
    setCreatedClient(null);
    reset();
  };

  if (createdClient?.success) {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-green-600 dark:text-green-400">
            Cliente Criado!
          </CardTitle>
          <CardDescription>
            O cliente foi criado com sucesso
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {createdClient.data && (
            <div className="space-y-4">
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Envie o código de ativação abaixo para o cliente
                </AlertDescription>
              </Alert>
              
              <div className="space-y-3 p-4 bg-muted rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">ID do Cliente:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{createdClient.data.clientId}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(createdClient.data.clientId)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Código de Ativação:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold">{createdClient.data.activationCode}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(createdClient.data.activationCode)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Expira em:</span>
                  <span className="font-medium">
                    {new Date(createdClient.data.expiresAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex gap-3">
            <Button onClick={handleCreateAnother} variant="outline" className="flex-1">
              Criar Outro Cliente
            </Button>
            <Button onClick={onSuccess} className="flex-1">
              Voltar ao Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <div className="flex items-center gap-2 mb-2">
          {onCancel && (
            <Button variant="ghost" size="icon" onClick={onCancel}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <UserPlus className="h-6 w-6 text-primary" />
          </div>
        </div>
        <CardTitle className="text-center">Criar Novo Cliente</CardTitle>
        <CardDescription className="text-center">
          Preencha os dados do cliente e selecione um plano
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Client Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Informações do Cliente</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome Completo</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    placeholder="Nome do cliente"
                    className={cn("pl-10", errors.name && "border-red-500")}
                    {...register('name')}
                  />
                </div>
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@exemplo.com"
                    className={cn("pl-10", errors.email && "border-red-500")}
                    {...register('email')}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefone (opcional)</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="phone"
                  placeholder="+55 11 99999-9999"
                  className="pl-10"
                  {...register('phone')}
                />
              </div>
            </div>
          </div>

          {/* Plan Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Plano</h3>
            
            <div className="space-y-2">
              <Label htmlFor="planId">Selecionar Plano</Label>
              <Select onValueChange={(value) => setValue('planId', value)}>
                <SelectTrigger className={errors.planId ? "border-red-500" : ""}>
                  <SelectValue placeholder="Escolha um plano" />
                </SelectTrigger>
                <SelectContent>
                  {dashboard?.availablePlans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{plan.name}</span>
                        <span className="text-muted-foreground ml-2">
                          R$ {plan.price.toFixed(2)}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.planId && (
                <p className="text-sm text-red-500">{errors.planId.message}</p>
              )}
            </div>

            {/* Plan Details */}
            {watch('planId') && dashboard?.availablePlans && (
              <div className="p-4 bg-muted rounded-lg">
                {(() => {
                  const selectedPlan = dashboard.availablePlans.find(p => p.id === watch('planId'));
                  if (!selectedPlan) return null;
                  
                  return (
                    <div className="space-y-2">
                      <h4 className="font-medium">{selectedPlan.name}</h4>
                      <p className="text-sm text-muted-foreground">{selectedPlan.description}</p>
                      <div className="flex items-center justify-between text-sm">
                        <span>Duração: {selectedPlan.duration} dias</span>
                        <span>Preço: R$ {selectedPlan.price.toFixed(2)}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {selectedPlan.features.map((feature, index) => (
                          <span key={index} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Activation Code */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Código de Ativação</h3>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="autoGenerate"
                checked={autoGenerate}
                onCheckedChange={(checked) => setValue('autoGenerate', checked)}
              />
              <Label htmlFor="autoGenerate">Gerar código automaticamente</Label>
            </div>

            {!autoGenerate && (
              <div className="space-y-2">
                <Label htmlFor="customCode">Código Personalizado</Label>
                <Input
                  id="customCode"
                  placeholder="MOOVA-CUSTOM-CODE"
                  className={errors.customCode ? "border-red-500" : ""}
                  {...register('customCode')}
                />
                {errors.customCode && (
                  <p className="text-sm text-red-500">{errors.customCode.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Use apenas letras maiúsculas, números e hífens
                </p>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            disabled={!isValid || isCreatingClient}
          >
            {isCreatingClient ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Criando Cliente...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Criar Cliente
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
